<template>
	<v-card variant="outlined" rounded="xl" class="sparky-report-card pa-3">
		<div
			v-if="variant === 'completed'"
			data-test-id="sparky-card-body"
			:class="{ 'cursor-pointer': isReportViewable(item) }"
			@click="isReportViewable(item) && emit('view', item)">
			<div class="d-flex align-start ga-2">
				<div class="report-name flex-grow-1">{{ item.name }}</div>
				<v-chip
					v-if="hasPartialResult(item) || hasExcludedDocuments(item)"
					size="x-small"
					color="warning"
					variant="tonal"
					class="flex-shrink-0"
					>Warning</v-chip
				>
				<v-chip v-else-if="item.status === AnalysisStatus.FAILED" size="x-small" color="error" variant="tonal" class="flex-shrink-0"
					>Failed</v-chip
				>
				<v-chip
					v-else-if="item.status === AnalysisStatus.WAITING_FOR_INPUT"
					size="x-small"
					color="teal"
					variant="tonal"
					class="flex-shrink-0"
					>Waiting for Input</v-chip
				>
				<v-chip v-else-if="isInitialReportReady(item)" size="x-small" color="teal" variant="tonal" class="flex-shrink-0"
					>Initial Report Ready</v-chip
				>
			</div>
			<div data-test-id="sparky-card-meta-row" class="d-flex align-center ga-2 mt-2">
				<v-chip v-if="item.owner" size="small" color="primary" variant="tonal" class="flex-shrink-0"
					>Session {{ item.owner }}</v-chip
				>
				<span class="text-caption text-medium-emphasis">
					{{ timezones.formatDateOnlyShort(item.createdAt) }} — {{ timezones.formatTimeOnly(item.createdAt) }}
				</span>
			</div>
			<div class="d-flex align-center justify-end ga-1 mt-2">
				<v-btn
					v-if="isReportViewable(item)"
					data-test-id="sparky-card-view"
					icon
					title="View"
					variant="plain"
					density="compact"
					@click.stop="emit('view', item)">
					<v-icon>icon-eye-stroke</v-icon>
				</v-btn>
				<v-tooltip v-if="isReportViewable(item)" location="top" :text="sharingDisabled ? SHARE_PERMISSION_MESSAGE : 'Share'">
					<template #activator="{ props: tooltipProps }">
						<span v-bind="tooltipProps" class="d-inline-flex" :tabindex="sharingDisabled ? 0 : undefined" @click.stop>
							<v-btn data-test-id="sparky-card-share" icon aria-label="Share" variant="plain" density="compact"
								:disabled="sharingDisabled" :aria-description="sharingDisabled ? SHARE_PERMISSION_MESSAGE : undefined"
								@click.stop="!sharingDisabled && emit('share', item)">
								<v-icon>icon-share-contact-stroke</v-icon>
							</v-btn>
						</span>
					</template>
				</v-tooltip>
				<!-- LSP-20160: a cancelling report keeps Cancel visible but disabled as "Cancelling…" — this
				     disables an in-progress action, not a form on validity. -->
				<v-btn
					v-if="showEditorActions && hasCancelControl(item.status)"
					data-test-id="sparky-card-cancel"
					icon
					:title="isCancelling(item) ? CANCELLING_LABEL : 'Cancel'"
					:aria-label="isCancelling(item) ? CANCELLING_LABEL : 'Cancel'"
					variant="plain"
					density="compact"
					:disabled="isCancelling(item)"
					:loading="cancellingIds.has(item.id)"
					@click.stop="emit('cancel', item)">
					<v-icon color="error">icon-close-circle-stroke</v-icon>
				</v-btn>
				<v-btn
					v-else-if="showEditorActions"
					data-test-id="sparky-card-delete"
					icon
					title="Delete"
					variant="plain"
					density="compact"
					@click.stop="emit('remove', item)">
					<v-icon color="error">icon-trash-full-stroke</v-icon>
				</v-btn>
			</div>
		</div>
		<template v-if="variant === 'active'">
			<div class="d-flex align-start ga-2">
				<div class="report-name report-name--single-line flex-grow-1">{{ item.name }}</div>
			</div>
			<div data-test-id="sparky-card-meta-row" class="d-flex flex-wrap align-center ga-2 mt-2">
				<v-chip v-if="item.isScheduled" size="small" color="warning" variant="tonal" class="flex-shrink-0">{{
					SCHEDULED_WAITING_FOR_MEDIA_LABEL
				}}</v-chip>
				<v-chip v-else size="small" color="primary" variant="tonal" class="flex-shrink-0">{{
					getInProgressLabel(item.status)
				}}</v-chip>
				<v-chip v-if="item.owner" size="small" color="primary" variant="tonal" class="flex-shrink-0"
					>Session {{ item.owner }}</v-chip
				>
				<span v-if="item.status === AnalysisStatus.WAITING_FOR_INPUT" class="text-caption text-medium-emphasis">-</span>
				<span v-else-if="!item.isScheduled" class="text-caption text-medium-emphasis d-flex align-center ga-1">
					<v-icon size="14">icon-clock-stroke</v-icon>
					{{ formatDurationShort(getElapsedSeconds(item)) }}
				</span>
			</div>
			<div class="d-flex align-center justify-end ga-1 mt-2">
				<v-btn
					v-if="isReportViewable(item)"
					data-test-id="sparky-card-view"
					icon
					title="View"
					variant="plain"
					density="compact"
					@click.stop="emit('view', item)">
					<v-icon>icon-eye-stroke</v-icon>
				</v-btn>
				<v-btn
					v-if="item.isScheduled && showEditorActions"
					data-test-id="sparky-card-cancel-schedule"
					variant="outlined"
					color="error"
					size="small"
					rounded="xl"
					:loading="cancellingScheduleIds.has(item._scheduledAnalysis?.id ?? '')"
					@click.stop="emit('cancelSchedule', item)">
					Cancel
				</v-btn>
				<v-btn
					v-else-if="showEditorActions && isRealTimeAnalysis(item)"
					data-test-id="sparky-card-finalize"
					variant="flat"
					color="primary"
					size="small"
					rounded="xl"
					:loading="finalizingIds.has(item.id)"
					@click.stop="emit('finalize', item)">
					Finalize
				</v-btn>
				<!-- LSP-20160: text is the label here, so no spinner — "Cancelling…" stays readable. -->
				<v-btn
					v-else-if="showEditorActions && hasCancelControl(item.status) && !isReportViewable(item)"
					data-test-id="sparky-card-cancel"
					variant="outlined"
					color="error"
					size="small"
					rounded="xl"
					:disabled="isCancelling(item)"
					@click.stop="emit('cancel', item)">
					{{ isCancelling(item) ? CANCELLING_LABEL : "Cancel" }}
				</v-btn>
			</div>
		</template>
	</v-card>
</template>

<script setup lang="ts">
	import { SCHEDULED_WAITING_FOR_MEDIA_LABEL } from "./sparkyReportTypes";
	import type { RowItem } from "./sparkyReportTypes";
	import { AnalysisStatus } from "@/types/scribe/Analysis";
	import { CANCELLING_LABEL, hasCancelControl } from "@/components/scribe/utils/analysisStatus";
	import timezones from "@/helpers/timezones";
	import { formatDurationShort } from "@/helpers/time";
	import { SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";

	defineProps<{
		item: RowItem;
		variant: "active" | "completed";
		showEditorActions: boolean;
		sharingDisabled?: boolean;
		cancellingIds: Set<string>;
		// LSP-20160: EntityReports' single "in flight or CANCELLING" predicate, so card and table agree.
		isCancelling: (item: RowItem) => boolean;
		cancellingScheduleIds: Set<string>;
		finalizingIds: Set<string>;
		isReportViewable: (item: RowItem) => boolean;
		isRealTimeAnalysis: (item: RowItem) => boolean;
		hasPartialResult: (item: RowItem) => boolean;
		hasExcludedDocuments: (item: RowItem) => boolean;
		isInitialReportReady: (item: RowItem) => boolean;
		getInProgressLabel: (status: AnalysisStatus) => string;
		getElapsedSeconds: (item: RowItem) => number;
	}>();

	const emit = defineEmits<{
		(e: "view", item: RowItem): void;
		(e: "remove", item: RowItem): void;
		(e: "cancel", item: RowItem): void;
		(e: "cancelSchedule", item: RowItem): void;
		(e: "finalize", item: RowItem): void;
		(e: "share", item: RowItem): void;
	}>();
</script>

<style scoped>
	/* LSP-15915 QA: default Vuetify outlined border (rgba(0,0,0,0.12)) was
	   reading as near-black on white card backgrounds. Override with an
	   explicitly lighter grey so the card edges recede instead of competing
	   with the content. */
	.sparky-report-card {
		border-color: rgba(0, 0, 0, 0.08) !important;
	}
	.report-name {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		font-weight: 600;
		font-size: 0.9375rem;
		line-height: 1.3;
	}
	/* Active cards force the name to a single line to bound card height. The
	   meta row below it (status pill + Session pill + elapsed) may still wrap
	   onto a second line when a long status chip and a Session chip can't
	   share a phone-width row. */
	.report-name--single-line {
		-webkit-line-clamp: 1;
	}
	.cursor-pointer {
		cursor: pointer;
	}
</style>
